import { DatePipe, NgFor, NgIf } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConnectDashboardService } from './connect-dashboard.service';
import { ConnectIconComponent } from './connect-icon.component';
import { formatCents } from './connect-dashboard.util';
import {
  documentKind,
  documentStory,
  docStatus,
  LedgerActivityEvent,
  LedgerDocument,
  ledgerEffectText,
  ledgerGroup,
  LedgerLine,
  LedgerLineTransaction,
  LedgerPosting,
  PatientLedger,
  settlementLabel,
  settlementTone,
  statusTone,
} from './connect-ledger.util';

export interface ConnectLedgerDialogData {
  groupId: string;
  /** The patient on Connect. */
  patientId: string;
  /** The patient here, when they are one of this practice's patients. */
  clinicalPatientId: string | null;
  /** A document to open and bring into view (a claim card's draft). */
  focusDocumentId?: string;
}

interface Drill<T> {
  expanded: boolean;
  loading: boolean;
  error: string | null;
  rows: T[] | null;
}

/**
 * A patient's account as MedicAI Connect shows it, read-only: who owes what, every claim and invoice with its lines and
 * what the scheme said, and the history. Nothing here changes anything on Connect.
 */
@Component({
  selector: 'app-connect-ledger-dialog',
  standalone: true,
  imports: [NgIf, NgFor, DatePipe, ConnectIconComponent],
  templateUrl: './connect-ledger-dialog.component.html',
  styleUrls: ['./connect-ledger-dialog.component.scss'],
  // Shares the Connect page's .cx-scope styles; its own rules sit under .cx-ledger.
  encapsulation: ViewEncapsulation.None,
})
export class ConnectLedgerDialogComponent implements OnInit, AfterViewChecked {
  ledger: PatientLedger | null = null;
  loading = true;
  error: string | null = null;
  activeTab: 'documents' | 'activity' = 'documents';

  readonly tabs = [
    { id: 'documents', label: 'Claims & invoices', icon: 'file-text' },
    { id: 'activity', label: 'History', icon: 'activity' },
  ] as const;

  readonly story = documentStory;
  readonly kind = documentKind;
  readonly docStatus = docStatus;
  readonly statusTone = statusTone;
  readonly settlementTone = settlementTone;
  readonly settlementLabel = settlementLabel;
  readonly effectText = ledgerEffectText;
  readonly groupOf = ledgerGroup;

  private readonly expandedDocs = new Set<string>();
  private readonly lineDrills = new Map<string, Drill<LedgerLineTransaction>>();
  private readonly entryDrills = new Map<string, Drill<LedgerPosting>>();
  private scrollToFocus = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: ConnectLedgerDialogData,
    private dialogRef: MatDialogRef<ConnectLedgerDialogComponent, 'patient-file'>,
    private connect: ConnectDashboardService,
    private host: ElementRef<HTMLElement>,
  ) {}

  get title(): string {
    const name = this.ledger?.patientName?.trim();
    return name ? `${name} · Patient ledger` : 'Patient ledger';
  }

  ngOnInit(): void {
    if (this.data.focusDocumentId) this.expandedDocs.add(this.data.focusDocumentId);
    this.load();
  }

  ngAfterViewChecked(): void {
    if (!this.scrollToFocus || !this.data.focusDocumentId) return;
    const panel = this.host.nativeElement.querySelector(`[data-doc="${this.data.focusDocumentId}"]`);
    if (panel) {
      this.scrollToFocus = false;
      panel.scrollIntoView({ block: 'start' });
    }
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.connect.ledger(this.data.groupId, this.data.patientId).subscribe({
      next: (ledger) => {
        this.ledger = ledger;
        this.loading = false;
        this.scrollToFocus = true;
      },
      error: () => {
        this.loading = false;
        this.error = 'MedicAI Connect could not be reached. Try again.';
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  openPatientFile(): void {
    this.dialogRef.close('patient-file');
  }

  rand(cents: number | null | undefined): string {
    return formatCents(cents ?? 0);
  }

  isDocOpen(id: string): boolean {
    return this.expandedDocs.has(id);
  }

  toggleDoc(id: string): void {
    if (this.expandedDocs.has(id)) this.expandedDocs.delete(id);
    else this.expandedDocs.add(id);
  }

  /** From a history entry: switch to the documents and open that one. */
  openDocument(documentId: string | null): void {
    if (!documentId) return;
    this.expandedDocs.add(documentId);
    this.activeTab = 'documents';
  }

  lineDrill(line: LedgerLine): Drill<LedgerLineTransaction> {
    return this.drill(this.lineDrills, line.id);
  }

  entryDrill(ev: LedgerActivityEvent): Drill<LedgerPosting> {
    return this.drill(this.entryDrills, ev.entryId);
  }

  toggleLine(line: LedgerLine): void {
    const d = this.lineDrill(line);
    d.expanded = !d.expanded;
    if (!d.expanded || d.rows || d.loading) return;
    d.loading = true;
    d.error = null;
    this.connect.transactions(this.data.groupId, this.data.patientId, line.id).subscribe({
      next: (res) => Object.assign(d, { rows: res.transactions, loading: false }),
      error: () => Object.assign(d, { error: 'Failed to load transactions', loading: false }),
    });
  }

  toggleEntry(ev: LedgerActivityEvent): void {
    const d = this.entryDrill(ev);
    d.expanded = !d.expanded;
    if (!d.expanded || d.rows || d.loading) return;
    d.loading = true;
    d.error = null;
    this.connect.postings(this.data.groupId, this.data.patientId, ev.entryId).subscribe({
      next: (res) => Object.assign(d, { rows: res.postings, loading: false }),
      error: () => Object.assign(d, { error: 'Failed to load postings', loading: false }),
    });
  }

  trackDoc(_: number, doc: LedgerDocument): string {
    return doc.id;
  }

  private drill<T>(map: Map<string, Drill<T>>, id: string): Drill<T> {
    let d = map.get(id);
    if (!d) {
      d = { expanded: false, loading: false, error: null, rows: null };
      map.set(id, d);
    }
    return d;
  }
}
